import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/** @format */

/**
 * External dependencies
 */
import { scaleBand as d3ScaleBand, scaleLinear as d3ScaleLinear, scaleTime as d3ScaleTime } from 'd3-scale';
import moment from 'moment';
/**
 * Describes getXScale
 * @param {array} uniqueDates - from `getUniqueDates`
 * @param {number} width - calculated width of the charting space
 * @param {boolean} compact - whether the chart must be compact (without padding
 between days)
 * @returns {function} a D3 scale of the dates
 */

export var getXScale = function getXScale(uniqueDates, width) {
  var compact = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return d3ScaleBand().domain(uniqueDates).range([0, width]).paddingInner(compact ? 0 : 0.1);
};
/**
 * Describes getXGroupScale
 * @param {array} orderedKeys - from `getOrderedKeys`
 * @param {function} xScale - from `getXScale`
 * @param {boolean} compact - whether the chart must be compact (without padding
 between days)
 * @returns {function} a D3 scale for each category within the xScale range
 */

export var getXGroupScale = function getXGroupScale(orderedKeys, xScale) {
  var compact = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return d3ScaleBand().domain(orderedKeys.filter(function (d) {
    return d.visible;
  }).map(function (d) {
    return d.key;
  })).rangeRound([0, xScale.bandwidth()]).padding(compact ? 0 : 0.07);
};
/**
 * Describes getXLineScale
 * @param {array} uniqueDates - from `getUniqueDates`
 * @param {number} width - calculated width of the charting space
 * @returns {function} a D3 scaletime for each date
 */

export var getXLineScale = function getXLineScale(uniqueDates, width) {
  return d3ScaleTime().domain([moment(uniqueDates[0], 'YYYY-MM-DD HH:mm').toDate(), moment(uniqueDates[uniqueDates.length - 1], 'YYYY-MM-DD HH:mm').toDate()]).rangeRound([0, width]);
};

var getYValueLimits = function getYValueLimits(data) {
  var maxYValue = Number.NEGATIVE_INFINITY;
  var minYValue = Number.POSITIVE_INFINITY;
  data.map(function (d) {
    for (var _i = 0, _Object$entries = Object.entries(d); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          key = _Object$entries$_i[0],
          item = _Object$entries$_i[1];

      if (key !== 'date' && Number.isFinite(item.value) && item.value > maxYValue) {
        maxYValue = item.value;
      }

      if (key !== 'date' && Number.isFinite(item.value) && item.value < minYValue) {
        minYValue = item.value;
      }
    }
  });
  return {
    upper: maxYValue,
    lower: minYValue
  };
};

export var calculateStep = function calculateStep(minValue, maxValue) {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return 1;
  }

  if (maxValue === 0 && minValue === 0) {
    return 1 / 3;
  }

  var maxAbsValue = Math.max(-minValue, maxValue);
  var maxLimit = 4 / 3 * maxAbsValue;
  var pow3Y = Math.pow(10, (Math.log(maxLimit) * Math.LOG10E + 1 | 0) - 2) * 3;
  var step = Math.ceil(maxLimit / pow3Y) * pow3Y / 3;

  if (maxValue < 1 && minValue > -1) {
    return Math.round(step * 4) / 4;
  }

  return Math.ceil(step);
};
/**
 * Returns the lower and upper limits of the Y scale and the calculated step to use in the axis, rounding
 * them to the nearest thousand, ten-thousand, million etc. In case it is a decimal number, ceils it.
 * @param {array} data - The chart component's `data` prop.
 * @returns {object} Object containing the `lower` and `upper` limits and a `step` value.
 */

export var getYScaleLimits = function getYScaleLimits(data) {
  var _getYValueLimits = getYValueLimits(data),
      minValue = _getYValueLimits.lower,
      maxValue = _getYValueLimits.upper;

  var step = calculateStep(minValue, maxValue);
  var limits = {
    lower: 0,
    upper: 0,
    step: step
  };

  if (Number.isFinite(minValue) || minValue < 0) {
    limits.lower = Math.floor(minValue / step) * step;

    if (limits.lower === minValue && minValue !== 0) {
      limits.lower -= step;
    }
  }

  if (Number.isFinite(maxValue) || maxValue > 0) {
    limits.upper = Math.ceil(maxValue / step) * step;

    if (limits.upper === maxValue && maxValue !== 0) {
      limits.upper += step;
    }
  }

  return limits;
};
/**
 * Describes getYScale
 * @param {number} height - calculated height of the charting space
 * @param {number} yMin - minimum y value
 * @param {number} yMax - maximum y value
 * @returns {function} the D3 linear scale from 0 to the value from `getYMax`
 */

export var getYScale = function getYScale(height, yMin, yMax) {
  return d3ScaleLinear().domain([Math.min(yMin, 0), yMax === 0 && yMin === 0 ? 1 : Math.max(yMax, 0)]).rangeRound([height, 0]);
};
//# sourceMappingURL=scales.js.map